import {
  create_policy,
  update_policy,
  delete_policy,
  recover_policy,
  query_policy_list,
} from '@/api/mjstore-model';
import { TableColumn } from '@/typing';
import { SearchConfig, Record } from '@/components/page-model/typing';
import ls from '@/utils/local-storage';
import { STORAGE_CURRENT_ORG_KEY } from '@/store/mutation-type';
import {
  slotList,
  btn_list,
  head_btn_list,
  event_obj,
  config,
} from '@/components/page-model/mjstore/mjstore-policy-config';

import { reactive } from 'vue';
import {get_is_active, get_is_active2, getDate} from '@/utils/function';

const None = null;
const True = true;
const False = false;

const current_org = ls.get(STORAGE_CURRENT_ORG_KEY); // 当前组织org

const search_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'inline',
  },
  fields: [
    {
      type: 'input',
      name: 'code',
      label: '促销政策编号',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写促销政策编号',
    },
    {
      type: 'input',
      name: 'name',
      label: '促销政策名称',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写促销政策名称',
    },
  ],
  rules: {},
  model: reactive({
    org_id: current_org?.id,
    name: null,
    code: null,
    status: null,
  }),
};
const search_options = {
  is_active: [
    { value: None, name: '全部' },
    { value: True, name: '可用' },
    { value: False, name: '作废' },
  ],
};
const edit_model_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'horizontal',
    col: config.creatCol,
    labelCol: {
      span: 6,
    },
    wrapperCol: {
      span: 18,
    },
  },
  fields: [
    {
      type: 'input',
      name: 'name',
      label: '促销政策名称',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写促销政策名称',
    },
    {
      type: 'rangepicker',
      name: 'interval_date',
      label: '活动日期区间',
      disabled: false,
      allowClear: true,
      inputType: 'number',
      defaultValue: '',
      showTime: false,
      placeholder: '请选择日期区间',
    },

    {
      type: 'textarea',
      name: 'mark',
      label: '备注',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写备注',
    },
  ],
  rules: {
    name: [{ required: true, message: '请填输入政策名称' }],
    interval_date: [{ required: true, message: '请选择日期区间' }],
  },
  model: reactive({
    org_id: current_org?.id,
    is_active: true,
    name: null,
    interval_date: null,
  }),
};
const edit_model_config_options = {
  status: [
    { value: true, name: '是' },
    { value: false, name: '否' },
  ],
};

const update_model_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'horizontal',
    col: config.creatCol,
    labelCol: {
      span: 6,
    },
    wrapperCol: {
      span: 18,
    },
  },
  fields: [
    {
      type: 'input',
      name: 'name',
      label: '促销政策名称',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写促销政策名称',
    },
    {
      type: 'rangepicker',
      name: 'interval_date',
      label: '活动日期区间',
      disabled: false,
      allowClear: true,
      inputType: 'number',
      defaultValue: '',
      showTime: false,
      valueFormat: 'YYYY-MM-DD',
      placeholder: '请选择活动日期区间',
    },

    {
      type: 'textarea',
      name: 'mark',
      label: '备注',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写备注',
    },
  ],
  rules: {
    name: [{ required: true, message: '请填输入政策名称' }],
    interval_date: [{ required: true, message: '请选择日期区间' }],
  },
  model: reactive({
    org_id: current_org?.id,
    is_active: true,
    name: null,
    interval_date: null,
  }),
};
const update_model_config_options = {
  status: [
    { value: true, name: '是' },
    { value: false, name: '否' },
  ],
};

const titleList = {
  title: '促销政策管理',
  addModalTitle: '新建促销政策',
  updateTitle: '修改促销政策',
  is_create: true, // 是否有【新建】按钮
  is_update: true, // 是否有【修改】按钮
  is_remove: true, // 是否有【删除】按钮
  is_recover: true, // 是否有【恢复】按钮
};

const api_fields: Record = {
  update: {
    policy_id: 'id',
  },
  delete: {
    policy_id: 'id',
  },
  recover: {
    policy_id: 'id',
  },
  updateData: {
    org_id: 'org_id',
    policy_id: 'id',
  },
};

const baseColumns: TableColumn[] = [
  {
    title: '序号',
    dataIndex: 'index',
    width: 50,
  },
  {
    title: '促销政策编号',
    dataIndex: 'code',
    checked: true,
  },
  {
    title: '促销政策名称',
    dataIndex: 'name',
    checked: true,
  },
  {
    title: '活动开始日期',
    dataIndex: 'start_time',
    checked: true,

    content: (record: any, text: any) => {
      return getDate(text);
    },
  },
  {
    title: '活动结束日期',
    dataIndex: 'end_time',
    checked: true,

    content: (record: any, text: any) => {
      return getDate(text);
    },
  },
  {
    title: '活动状态',
    dataIndex: 'status',
    checked: true,
    cellComponent: 'boolean',
    name1: '活动中',
    name2: '已到期',
    filterMultiple: false,
    filters: [
      { text: '活动中', value: true },
      { text: '已到期', value: false },
    ],
    content: (record: any, text: any) => {
      const dict: any = {
        true: '活动中',
        false: '已到期',
      };
      return dict[text];
    },
  },
  {
    title: '备注',
    dataIndex: 'mark',
    checked: true,
  },
  {
    title: '操作',
    width: 200,
    dataIndex: 'is_active',
    checked: true,
    cellComponent: 'action',
    fixed: 'right',
    filterMultiple: false,
    filters: [
      { text: '全部', value: '' },
      { text: '可用', value: true },
      { text: '作废', value: false },
    ],
    content: (record: any, text: any) => {
      return get_is_active2(text);
    },
  },
];
const newSlotList: any = [];
Object.assign(newSlotList, slotList);
newSlotList.push(
  {
    type: 'custom',
    // slotName: 'is_active',
    field_true: '可用',
    field_false: '作废',
  },
  {
    type: 'custom',
    slotName: 'status',
    field_true: '活动中',
    field_false: '已作废',
  },
);

export default () => {
  return {
    searchConfig: search_config,
    searchConfigOptions: search_options,
    formModalConfig: edit_model_config,
    formModalConfigOptions: edit_model_config_options,
    updateFormModalConfig: update_model_config,
    updateFormModalConfigOptions: update_model_config_options,
    queryData: query_policy_list,
    addData: create_policy,
    updateData: update_policy,
    removeData: delete_policy,
    recoverData: recover_policy,
    baseColumns: baseColumns,
    slotList: newSlotList,
    titleList: titleList,
    api_fields: api_fields,
    btn_list: btn_list,
    head_btn_list: head_btn_list,
    event_obj: event_obj,
  };
};
